import { useEffect, useMemo, useRef, useState } from "react";

import { api, getApiSourceInfo } from "../api.js";
import {
  endOfDayDatetimeLocal,
  filterDateTimeDisplayValue,
  nowDatetimeLocal,
  prevFourCompleteWeeksRange,
  startOfDayDatetimeLocal,
} from "../lib/filterDates.js";
import type {
  FilterOptions,
  FilterState,
  GameFilterOption,
  MeFleet,
  Timeframe,
} from "../types.js";

const DAY_MS = 86400000;

interface FilterBarProps {
  disableGame?: boolean;
  filters: FilterState;
  reportingTimezoneLabel?: string;
  /** Fleets the signed-in user may filter by; the picker is hidden unless there is a choice. */
  fleets: MeFleet[];
  onFiltersChange: (filters: FilterState) => void;
  onReset: () => void;
}

function gameLabel(v: string): string {
  const routeMatch = v.match(/\/games\/([^/]+)\/?/i);
  const raw = routeMatch?.[1] ?? v;
  const normalized = raw
    .replace(/index\.html?$/i, "")
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .trim();
  if (!normalized) return v;
  return normalized
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function parseSgSlugs(value: string): string[] {
  if (!value.startsWith("sg:")) return [];
  return value
    .slice(3)
    .split(",")
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);
}

/** Drop exact duplicate `value` rows only (API should be one row per slug). */
function dedupeGameFilterOptionsByValue(list: GameFilterOption[]): GameFilterOption[] {
  const seen = new Map<string, GameFilterOption>();
  for (const g of list) {
    if (!seen.has(g.value)) {
      seen.set(g.value, g);
    }
  }
  return [...seen.values()].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" }) ||
    a.value.localeCompare(b.value),
  );
}

export function FilterBar({
  disableGame = false,
  filters,
  reportingTimezoneLabel,
  fleets,
  onFiltersChange,
  onReset,
}: FilterBarProps) {
  const [options, setOptions] = useState<FilterOptions>({ venues: [], devices: [], gameOptions: [] });
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const mergedGameOptions = useMemo(
    () => dedupeGameFilterOptionsByValue(options.gameOptions),
    [options.gameOptions],
  );
  const gameValueToLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of mergedGameOptions) {
      m.set(g.value, g.label);
    }
    return m;
  }, [mergedGameOptions]);

  const apiSourceBaseUrl = getApiSourceInfo().baseUrl;

  const visibleDevices = useMemo(() => {
    const venueById = new Map(options.venues.map((v) => [v.id, v]));
    let list =
      filters.venueId === "all"
        ? options.devices
        : options.devices.filter((d) => d.venueId === filters.venueId);
    if (filters.venueId === "all" && !filters.includeNonPublic) {
      list = list.filter((d) => {
        if (!d.venueId) return true;
        const venue = venueById.get(d.venueId);
        return venue?.isPublic !== false;
      });
    }
    return [...list].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }),
    );
  }, [options.devices, options.venues, filters.venueId, filters.includeNonPublic]);

  const { publicVenues, nonPublicVenues } = useMemo(() => {
    const publicList: typeof options.venues = [];
    const nonPublicList: typeof options.venues = [];
    for (const v of options.venues) {
      if (v.isPublic === false) nonPublicList.push(v);
      else publicList.push(v);
    }
    const byName = (a: { name: string }, b: { name: string }) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    publicList.sort(byName);
    nonPublicList.sort(byName);
    return { publicVenues: publicList, nonPublicVenues: nonPublicList };
  }, [options.venues]);

  useEffect(() => {
    api.filters(filters.fleetId)
      .then((incoming) => {
        setOptions({
          venues: (incoming.venues ?? []).map((v) => ({
            id: v.id,
            name: v.name,
            isPublic:
              v.isPublic !== false &&
              (v as { is_public?: boolean }).is_public !== false,
          })),
          devices: (incoming.devices ?? []).map((d) => ({
            id: d.id,
            name: d.name,
            venueId: d.venueId ?? (d as { venue_id?: string }).venue_id,
          })),
          gameOptions: incoming.gameOptions ?? [],
        });
      })
      .catch(() => {});
  }, [apiSourceBaseUrl, filters.fleetId]);

  /** A venue or device from another fleet would AND with the new fleet and return nothing. */
  useEffect(() => {
    if (filters.venueId === "all" && filters.deviceId === "all") return;
    if (options.venues.length === 0) return;
    const venueOk = filters.venueId === "all" || options.venues.some((v) => v.id === filters.venueId);
    const deviceOk =
      filters.deviceId === "all" || options.devices.some((d) => d.id === filters.deviceId);
    if (venueOk && deviceOk) return;
    onFiltersChange({
      ...filtersRef.current,
      venueId: venueOk ? filtersRef.current.venueId : "all",
      deviceId: deviceOk ? filtersRef.current.deviceId : "all",
    });
  }, [options.venues, options.devices, filters.venueId, filters.deviceId, onFiltersChange]);

  /** Stale device_id from URL/bookmark for another venue would AND with venue_id and return no rows. */
  useEffect(() => {
    if (filters.deviceId === "all") return;
    if (filters.venueId === "all") return;
    if (visibleDevices.some((d) => d.id === filters.deviceId)) return;
    onFiltersChange({ ...filtersRef.current, deviceId: "all" });
  }, [filters.deviceId, filters.venueId, visibleDevices, onFiltersChange]);

  /** If `game_url` was a subset slug list, align it to the merged `sg:` value so the select matches. */
  useEffect(() => {
    const g = filters.gameUrl;
    if (g === "all" || !g.startsWith("sg:")) return;
    const allowed = new Set(mergedGameOptions.map((x) => x.value));
    if (allowed.has(g)) return;
    const selected = parseSgSlugs(g);
    if (selected.length === 0) return;
    for (const opt of mergedGameOptions) {
      if (!opt.value.startsWith("sg:")) continue;
      const slugs = parseSgSlugs(opt.value);
      if (selected.every((s) => slugs.includes(s))) {
        if (opt.value !== g) {
          onFiltersChange({ ...filtersRef.current, gameUrl: opt.value });
        }
        return;
      }
    }
  }, [mergedGameOptions, filters.gameUrl, onFiltersChange]);

  const update = (patch: Partial<FilterState>) => {
    let next = { ...filters, ...patch };
    if (patch.venueId !== undefined && patch.venueId !== filters.venueId) {
      next = { ...next, deviceId: "all" };
    }
    onFiltersChange(next);
  };

  const handleTimeframeChange = (timeframe: Timeframe) => {
    const now = Date.now();
    let from = filters.from;
    let to = nowDatetimeLocal();
    if (timeframe === "today") {
      from = startOfDayDatetimeLocal(now);
    } else if (timeframe === "yesterday") {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      from = startOfDayDatetimeLocal(d.getTime());
      to = endOfDayDatetimeLocal(d.getTime());
    } else if (timeframe === "7d") {
      from = startOfDayDatetimeLocal(now - 7 * DAY_MS);
    } else if (timeframe === "30d") {
      from = startOfDayDatetimeLocal(now - 30 * DAY_MS);
    } else if (timeframe === "prev4w") {
      const range = prevFourCompleteWeeksRange(now);
      from = range.from;
      to = range.to;
    }
    onFiltersChange({ ...filters, timeframe, from, to });
  };

  const handleDateChange = (key: "from" | "to", value: string) => {
    update({ [key]: value, timeframe: "custom" as Timeframe });
  };

  return (
    <div className="sticky top-2 z-10 mb-3 flex flex-nowrap items-start gap-1.5 overflow-x-auto rounded-xl border border-border bg-panel p-2">
      {fleets.length > 1 && (
        <div className="flex min-w-[5.5rem] flex-1 flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wide text-text-secondary">Fleet</label>
          <select
            className="w-full min-w-0 rounded-lg border border-border-subtle bg-panel-2 px-1.5 py-2 text-[12px] text-text-primary"
            value={filters.fleetId}
            onChange={(e) => update({ fleetId: e.target.value })}
          >
            <option value="all" className="font-bold uppercase">ALL</option>
            {fleets.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="flex min-w-[5.5rem] flex-1 flex-col gap-1">
        <label className="text-[10px] uppercase tracking-wide text-text-secondary">Venue</label>
        <select
          className="w-full min-w-0 rounded-lg border border-border-subtle bg-panel-2 px-1.5 py-2 text-[12px] text-text-primary"
          value={filters.venueId}
          onChange={(e) => update({ venueId: e.target.value })}
        >
          <option value="all" className="font-bold uppercase">ALL</option>
          {publicVenues.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
          {nonPublicVenues.length > 0 ? (
            <>
              <option disabled>── Non-public ──</option>
              {nonPublicVenues.map((v) => (
                <option key={v.id} value={v.id}>
                  {`- ${v.name}`}
                </option>
              ))}
            </>
          ) : null}
        </select>
        {nonPublicVenues.length > 0 ? (
          <label
            className="flex cursor-pointer items-center gap-1 text-[10px] leading-tight text-text-secondary"
            title="Include venues marked not public in ALL aggregates and the Table list"
          >
            <input
              type="checkbox"
              className="rounded border-border-subtle"
              checked={filters.includeNonPublic}
              onChange={(e) => update({ includeNonPublic: e.target.checked })}
            />
            Include non-public
          </label>
        ) : null}
      </div>
      <fieldset disabled={disableGame} className="min-w-[4.5rem] flex-1">
        <FilterSelect
          className="w-full min-w-0"
          label={disableGame ? "Game (not applicable)" : "Game"}
          value={filters.gameUrl}
          options={["all", ...mergedGameOptions.map((g) => g.value)]}
          displayLabel={(o) => (o === "all" ? "ALL" : gameValueToLabel.get(o) ?? gameLabel(o))}
          onChange={(v) => update({ gameUrl: v })}
        />
      </fieldset>
      <FilterSelect
        className="w-[6.25rem] shrink-0"
        compact
        label="Timeframe"
        value={filters.timeframe}
        options={["today", "yesterday", "7d", "30d", "prev4w", "custom"]}
        displayLabel={(v) =>
          v === "today"
            ? "Today"
            : v === "yesterday"
              ? "Yesterday"
              : v === "7d"
                ? "7d"
                : v === "30d"
                  ? "30d"
                  : v === "prev4w"
                    ? "Prev 4w"
                    : "Custom"
        }
        onChange={(v) => handleTimeframeChange(v as Timeframe)}
      />
      <div className="flex min-w-[11rem] flex-[1.6] flex-col gap-1">
        <div className="grid min-w-0 grid-cols-2 gap-1.5">
          <FilterInput
            label="From"
            type="datetime-local"
            value={filters.from}
            onChange={(v) => handleDateChange("from", v)}
          />
          <FilterInput
            label="To"
            type="datetime-local"
            value={filters.to}
            onChange={(v) => handleDateChange("to", v)}
          />
        </div>
        {reportingTimezoneLabel ? (
          <span
            className="truncate text-[10px] leading-tight text-text-secondary"
            title={`Filter times use reporting timezone: ${reportingTimezoneLabel}`}
          >
            Times in {reportingTimezoneLabel}
          </span>
        ) : null}
      </div>
      <FilterSelect
        className="min-w-[5.5rem] flex-1"
        label="Table"
        value={
          filters.deviceId !== "all" && !visibleDevices.some((d) => d.id === filters.deviceId)
            ? "all"
            : filters.deviceId
        }
        options={["all", ...visibleDevices.map((d) => d.id)]}
        displayLabel={(o) => {
          if (o === "all") return "ALL";
          const device = visibleDevices.find((d) => d.id === o);
          if (!device) return o;
          const name = device.name.trim() || "—";
          const venue =
            options.venues.find((v) => v.id === device.venueId)?.name?.trim() ?? "";
          const full = venue ? `${name} · ${venue}` : name;
          return full.length <= 48 ? full : `${full.slice(0, 47)}…`;
        }}
        onChange={(v) => update({ deviceId: v })}
      />
      <div className="flex shrink-0 flex-col gap-1">
        <label className="text-[10px] uppercase tracking-wide text-text-secondary">Reset</label>
        <button
          className="whitespace-nowrap rounded-lg border border-[#426aa8] bg-[#223455] px-2 py-2 text-[12px] font-semibold text-text-primary hover:bg-[#29416b]"
          onClick={onReset}
        >
          Reset filters
        </button>
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  options,
  displayLabel,
  onChange,
  className,
  compact = false,
}: {
  label: string;
  value: string;
  options: string[];
  displayLabel?: (v: string) => string;
  onChange: (v: string) => void;
  className?: string;
  compact?: boolean;
}) {
  const effectiveOptions =
    value === "all" || options.includes(value) ? options : [...options, value];

  return (
    <div className={`flex min-w-0 flex-col gap-1 ${className ?? ""}`}>
      <label
        className={`text-[10px] uppercase tracking-wide text-text-secondary ${compact ? "truncate" : ""}`}
        title={label}
      >
        {label}
      </label>
      <select
        className={`w-full min-w-0 rounded-lg border border-border-subtle bg-panel-2 text-text-primary ${compact ? "px-1 py-2 text-[12px]" : "px-1.5 py-2 text-[12px]"}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {effectiveOptions.map((o) => (
          <option key={o} value={o} className={o === "all" ? "font-bold uppercase" : undefined}>
            {displayLabel ? displayLabel(o) : o}
          </option>
        ))}
      </select>
    </div>
  );
}

function FilterInput({
  label,
  type,
  value,
  onChange,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const isDateTime = type === "datetime-local" || type === "date";
  const displayValue = type === "datetime-local" ? filterDateTimeDisplayValue(value) : value;
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label className="text-[10px] uppercase tracking-wide text-text-secondary">{label}</label>
      <input
        type={type}
        step={type === "datetime-local" && /T\d{2}:\d{2}:/.test(displayValue) ? 0.001 : undefined}
        className={`w-full min-w-0 rounded-lg border border-border-subtle bg-panel-2 px-1 py-2 text-[11px] text-text-primary${isDateTime ? " filter-date-input" : ""}`}
        value={displayValue}
        title={displayValue !== value ? `Exact time: ${value}` : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
